import { Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { LabelValueRow } from '../LabelValueRow';
import type { PotAutoSubmit, PotDeadlines } from '../../types/potDetails.types';

interface PotStatusDeadlinesCardProps {
  deadlines: PotDeadlines | null;
  autoSubmit: PotAutoSubmit;
}

export function PotStatusDeadlinesCard({
  deadlines,
  autoSubmit,
}: PotStatusDeadlinesCardProps) {
  const formatDateShort = (dateString: string) =>
    format(parseISO(dateString), 'dd MMM yyyy');

  return (
    <Card
      variant="outlined"
      data-testid="pot-status-deadlines-card"
      sx={{ height: '100%' }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } },
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Deadlines
        </Typography>

        {deadlines && (
          <Stack spacing={0.5}>
            <LabelValueRow
              label="Book-in by"
              value={formatDateShort(deadlines.bookInBy)}
            />
            <LabelValueRow
              label="Verify by"
              value={formatDateShort(deadlines.verifyBy)}
            />
            <LabelValueRow
              label="Submit by"
              value={formatDateShort(deadlines.submitBy)}
            />
          </Stack>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.5}>
          <LabelValueRow
            label="Auto-submit"
            value={
              <Typography
                variant="body2"
                color={autoSubmit.isScheduled ? 'info.main' : 'text.secondary'}
                fontWeight={autoSubmit.isScheduled ? 500 : undefined}
                data-testid="pot-auto-submit-status"
              >
                {autoSubmit.isScheduled ? 'Scheduled' : 'Not scheduled'}
              </Typography>
            }
          />
          <LabelValueRow
            label="Recurring"
            value={
              <Typography
                variant="body2"
                color={
                  autoSubmit.isRecurringEnabled
                    ? 'success.main'
                    : 'text.secondary'
                }
                fontWeight={autoSubmit.isRecurringEnabled ? 500 : undefined}
                data-testid="pot-recurring-auto-submit-status"
              >
                {autoSubmit.isRecurringEnabled ? 'Enabled' : 'Off'}
              </Typography>
            }
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
